import React, { useEffect, useState } from "react";
import axios from "axios";
import { LineChart, Line, AreaChart, Area, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { TrendingUp } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => Number(n || 0).toLocaleString("en-US");

// Cumulative profit chart shown in the empty white space on the admin
// home (when the tile-menu modal is closed). Like a stock-trading line
// showing where the business started from and the upward trend.
const ProfitChart = () => {
  const [days, setDays] = useState([]);
  const [range, setRange] = useState(30); // 7 / 30 / 90 / 365
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchSeries(); /* eslint-disable-next-line */ }, [range]);
  const fetchSeries = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/profit-timeseries`, { params: { days: range } });
      setDays(data.days || []);
    } catch (_) { /* silent */ }
    finally { setLoading(false); }
  };

  // Build cumulative running total
  let running = 0;
  const data = days.map((d) => {
    running += Number(d.profit) || 0;
    return {
      day: d.day.slice(5), // MM-DD
      cumulative: running,
      profit: d.profit,
      revenue: d.revenue,
      cartons: d.cartons,
    };
  });

  const finalCumulative = data.length > 0 ? data[data.length - 1].cumulative : 0;
  const firstCumulative = data.length > 0 ? data[0].cumulative : 0;
  const growth = finalCumulative - firstCumulative;

  return (
    <div className="bg-white rounded-2xl border border-[#E4EBE6] p-4 space-y-3" dir="rtl" data-testid="profit-chart-card">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-[#15803D]" />
          <h3 className="font-black text-[#1E3A5F]">نمو الأرباح التراكمي</h3>
        </div>
        <div className="flex items-center gap-1 bg-[#F3F6F4] rounded-full p-1">
          {[
            { v: 7, l: "أسبوع" },
            { v: 30, l: "شهر" },
            { v: 90, l: "3 أشهر" },
            { v: 365, l: "سنة" },
          ].map((opt) => (
            <button
              key={opt.v}
              type="button"
              onClick={() => setRange(opt.v)}
              data-testid={`chart-range-${opt.v}`}
              className={`px-3 py-1 rounded-full text-[11px] font-bold transition-colors ${
                range === opt.v ? "bg-white text-[#1E3A5F] shadow" : "text-[#5C6E64]"
              }`}
            >
              {opt.l}
            </button>
          ))}
        </div>
      </div>

      {/* Quick KPIs */}
      <div className="grid grid-cols-3 gap-2 text-center">
        <div className="rounded-lg p-2 bg-[#1E3A5F]/5 border border-[#1E3A5F]/15">
          <p className="text-[10px] text-[#5C6E64]">من البداية</p>
          <p className="font-black text-sm text-[#1E3A5F]" dir="ltr">{fmt(firstCumulative)}</p>
        </div>
        <div className="rounded-lg p-2 bg-[#15803D]/10 border border-[#15803D]/25">
          <p className="text-[10px] text-[#5C6E64]">النمو خلال الفترة</p>
          <p className="font-black text-sm text-[#15803D]" dir="ltr">+{fmt(growth)}</p>
        </div>
        <div className="rounded-lg p-2 bg-[#F59E0B]/10 border border-[#F59E0B]/30">
          <p className="text-[10px] text-[#5C6E64]">حالياً</p>
          <p className="font-black text-sm text-[#B45309]" dir="ltr">{fmt(finalCumulative)}</p>
        </div>
      </div>

      <div className="h-56 w-full">
        {loading ? (
          <div className="h-full w-full flex items-center justify-center text-[#5C6E64] text-sm">جاري التحميل...</div>
        ) : data.length === 0 ? (
          <div className="h-full w-full flex items-center justify-center text-[#5C6E64] text-sm">لا توجد بيانات بعد</div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="profitArea" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#15803D" stopOpacity={0.45} />
                  <stop offset="95%" stopColor="#15803D" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#E4EBE6" />
              <XAxis dataKey="day" tick={{ fontSize: 10, fill: "#5C6E64" }} />
              <YAxis tick={{ fontSize: 10, fill: "#5C6E64" }} width={50} tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(0)}K` : v} />
              <Tooltip
                formatter={(value, name) => [`${fmt(value)} د.ع`, name === "cumulative" ? "تراكمي" : name === "profit" ? "ربح اليوم" : name]}
                labelFormatter={(label) => `يوم: ${label}`}
                contentStyle={{ direction: "rtl", fontSize: 12, borderRadius: 8 }}
              />
              <Area type="monotone" dataKey="cumulative" stroke="#15803D" strokeWidth={2.5} fill="url(#profitArea)" name="cumulative" />
              <Line type="monotone" dataKey="profit" stroke="#1E3A5F" strokeWidth={1.5} dot={false} name="profit" />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
};

export default ProfitChart;
